<template>
    <vx-expandable-card>
        <template v-slot:header>Payment Method</template>
        <template v-slot:content>
            <div class="flex flex-col gap-4 mt-3">
                <template v-if="!isEditMode">
                    <div class="border-2 border-dashed border-gray-300 rounded-lg p-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <font-awesome-icon widthAuto :icon="cardIcon" class="text-gray-500"></font-awesome-icon>
                                <span
                                    v-if="currentPaymentMethod.card_last_four"
                                    class="text-sm font-medium text-gray-700"
                                >
                                    {{ normalizedCardBrand }} •••• {{ currentPaymentMethod.card_last_four }}
                                </span>
                                <span v-else class="text-sm font-medium text-gray-700">No payment card on file</span>
                            </div>
                            <vx-button
                                size="sm"
                                @click="enterEditMode"
                                color="primary"
                                :disabled="!stripeReady"
                                data-test="payment-method-button"
                            >
                                {{ currentPaymentMethod.card_last_four ? 'Update' : 'Add Card' }}
                            </vx-button>
                        </div>
                    </div>
                </template>

                <div v-show="isEditMode">
                    <div class="flex flex-col gap-4">
                        <div>
                            <vx-label size="md"> Cardholder's Name </vx-label>

                            <vx-input
                                size="lg"
                                v-model="cardholderName"
                                :error="displayFirstError(validate.cardholderName.$errors)"
                                data-test="cardholder-name-input"
                            ></vx-input>
                        </div>

                        <div>
                            <vx-label size="md"> Card </vx-label>

                            <div
                                :id="paymentCardElementId"
                                class="block w-full appearance-none border border-solid bg-white font-sans text-slate-900 transition focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 text-base rounded-xl h-12 p-4"
                                :class="{
                                    'border-rose-300': cardError,
                                    'border-slate-300': !cardError,
                                }"
                            ></div>

                            <span class="mt-2 px-0.5 text-xs text-rose-800" v-show="cardError">
                                {{ cardError }}
                            </span>
                        </div>

                        <div class="flex gap-2 mt-1">
                            <vx-button
                                size="lg"
                                @click="cancelEdit"
                                color="muted"
                                class="flex-1"
                                data-test="cancel-button"
                            >
                                Cancel
                            </vx-button>
                            <vx-button
                                size="lg"
                                @click="update"
                                :loading="isUpdating"
                                class="flex-1"
                                data-test="update-button"
                            >
                                Update
                            </vx-button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import {
    faCcAmex,
    faCcDinersClub,
    faCcDiscover,
    faCcJcb,
    faCcMastercard,
    faCcVisa,
} from '@fortawesome/free-brands-svg-icons';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxExpandableCard, VxInput, VxLabel } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { ref, computed, onMounted, nextTick } from 'vue';
import { groupBillingClient } from '~/services';
import axios from '~/services/axios';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { displayFirstError } from '~/utils/validation';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
    paymentMethod: {
        type: Object,
        required: true,
    },
    stripeKey: {
        type: String,
        required: true,
    },
    stripeApiVersion: {
        type: String,
        required: true,
    },
});

const toasted = useToasted();
const currentPaymentMethod = ref({ ...props.paymentMethod });
const isEditMode = ref(false);
const cardElement = ref(null);
const isUpdating = ref(false);
const cardholderName = ref('');
const cardError = ref('');
const cardElementValidation = ref({
    empty: true,
    complete: false,
    error: undefined,
});

const stripe = ref(null);
const stripeReady = ref(false);

const paymentCardElementId = `payment-card-element-${Math.random().toString(36).substring(2, 15)}`;

const validate = useVuelidate(
    {
        cardholderName: { required },
    },
    {
        cardholderName,
    }
);

const cardIcon = computed(() => {
    if (!currentPaymentMethod.value.card_brand || !currentPaymentMethod.value.card_last_four) {
        return faCreditCard;
    }

    switch (currentPaymentMethod.value.card_brand) {
        case 'amex':
        case 'Amex':
        case 'American Express':
            return faCcAmex;
        case 'diners':
        case 'Diners':
        case 'Diners Club':
            return faCcDinersClub;
        case 'discover':
        case 'Discover':
            return faCcDiscover;
        case 'jcb':
        case 'JCB':
            return faCcJcb;
        case 'mastercard':
        case 'Mastercard':
        case 'MasterCard':
            return faCcMastercard;
        case 'visa':
        case 'Visa':
            return faCcVisa;
        default:
            return faCreditCard;
    }
});

const normalizedCardBrand = computed(() => {
    if (!currentPaymentMethod.value.card_brand) {
        return 'Card';
    }

    switch (currentPaymentMethod.value.card_brand) {
        case 'amex':
        case 'Amex':
        case 'American Express':
            return 'American Express';
        case 'diners':
        case 'Diners':
        case 'Diners Club':
            return 'Diners Club';
        case 'discover':
        case 'Discover':
            return 'Discover';
        case 'jcb':
        case 'JCB':
            return 'JCB';
        case 'mastercard':
        case 'Mastercard':
        case 'MasterCard':
            return 'Mastercard';
        case 'visa':
        case 'Visa':
            return 'Visa';
        default:
            return currentPaymentMethod.value.card_brand;
    }
});

const enterEditMode = async () => {
    isEditMode.value = true;
    cardError.value = '';
    cardholderName.value = '';

    await nextTick();

    if (!cardElement.value && stripe.value) {
        cardElement.value = createCardElement(`#${paymentCardElementId}`);
    } else if (cardElement.value) {
        cardElement.value.clear();
    }
};

const cancelEdit = () => {
    isEditMode.value = false;
    cardError.value = '';
    cardholderName.value = '';

    if (cardElement.value) {
        cardElement.value.clear();
    }
};

const createCardElement = (container) => {
    if (!stripe.value) {
        console.error('Stripe not initialized');
        return null;
    }

    const card = stripe.value.elements().create('card', {
        hideIcon: true,
        hidePostalCode: true,
    });

    card.mount(container);

    card.on('change', (event) => {
        cardElementValidation.value = {
            ...cardElementValidation.value,
            ...event,
        };
    });

    return card;
};

const generateToken = async () => {
    const response = await axios.get('/stripe/token');
    return response.data?.clientSecret;
};

const update = async () => {
    if (isUpdating.value) {
        return;
    }

    isUpdating.value = true;
    cardError.value = '';
    validate.value.$reset();

    const isCardElementValid =
        !cardElementValidation.value.empty &&
        cardElementValidation.value.complete &&
        !cardElementValidation.value.error?.message;

    if (!(await validate.value.$validate()) || !isCardElementValid) {
        isUpdating.value = false;

        if (!isCardElementValid) {
            cardError.value = cardElementValidation.value.error?.message || 'Your card information is incomplete.';
        }

        return;
    }

    const payload = {
        name: cardholderName.value,
    };

    try {
        const secret = await generateToken();
        const stripeResponse = await stripe.value.handleCardSetup(secret, cardElement.value, {
            payment_method_data: {
                billing_details: payload,
            },
        });

        if (stripeResponse.error) {
            cardError.value = stripeResponse.error.message;
        } else {
            await sendUpdateToServer(stripeResponse.setupIntent.payment_method);
            toasted.success('Your payment method has been updated successfully');
            isEditMode.value = false;
            if (cardElement.value) {
                cardElement.value.clear();
            }
        }
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        isUpdating.value = false;
    }
};

const sendUpdateToServer = async (paymentMethod) => {
    const response = await groupBillingClient.updatePaymentMethod(props.teamId, props.groupId, paymentMethod);

    currentPaymentMethod.value = response.data;

    cardholderName.value = '';
    validate.value.$reset();

    return response;
};

onMounted(async () => {
    const checkStripe = setInterval(() => {
        if (window.Stripe && props.stripeKey) {
            stripe.value = window.Stripe(props.stripeKey, {
                apiVersion: props.stripeApiVersion,
            });

            stripeReady.value = true;

            clearInterval(checkStripe);
        }
    }, 100);

    setTimeout(() => {
        clearInterval(checkStripe);

        if (!stripe.value) {
            console.error('Stripe failed to load');
            toasted.error('Payment card form failed to load. Please refresh the page.');
        }
    }, 5000);
});
</script>
